/* eslint-disable no-console */
import React, { useCallback, useMemo } from 'react';
import T from 'i18n-react';

import { MultiSelect } from '@motorway/motorway-storybook-cra';

import { formatAsSelectOptions } from 'Components/VehicleList/VehicleList.helpers';
import {
	triggerModelDropdownExpandedEvent,
	triggerModelSearchInputEvent,
	triggerModelSelectedEvent,
} from 'Services/analytics/events';
import { useSetFilter } from 'Stores/FilterStore/VehicleListFilter/VehicleListFilterStore';
import { APP_THEME } from 'Utilities/consts';
import useModelsFilter from 'Utilities/hooks/models/useModelsFilter';
import { useValueChangedCount } from 'Utilities/hooks/useValueChangedCount';

import { mapValuesToSelect, onMultiSelectChange, triggerEvent, useMultiSelectInputValues } from '../Filters.helpers';

import { sortModels } from './ModelMultiSelect.helpers';

const MAKE_MODEL_PLACEHOLDER_VALUE = [{ text: T.translate('vehicleList.filters.modelPlaceholder') }];

export const onModelDropdownFocus = () => {
	triggerModelDropdownExpandedEvent();
};

export const onModelDropdownSelect = ({ text }, { value }) => {
	const isSelected = value.some((v) => v.text === text);
	triggerModelSelectedEvent({ isSelected, text });
};

const ModelMultiSelect = () => {
	const { model, modelConfigId, modelsBySelectedMakes } = useModelsFilter();
	const setFilter = useSetFilter();
	const { prevModelSelectInputValue, setPrevModelSelectInputValue } = useMultiSelectInputValues();

	const onModelSelectBlur = () => {
		setPrevModelSelectInputValue('');
	};

	const onModelSelectChange = onMultiSelectChange({
		callback: triggerEvent({
			previousValue: prevModelSelectInputValue,
			setPreviousValue: setPrevModelSelectInputValue,
			trigger: triggerModelSearchInputEvent,
		}),
		setValue: setFilter,
	});

	const formattedModels = useMemo(
		() =>
			formatAsSelectOptions({
				options: sortModels(modelsBySelectedMakes),
				text: 'namePretty',
				textSuffix: 'total',
				value: 'id',
			}),
		[modelsBySelectedMakes],
	);

	/*
    Multiselect placeholder does not work with empty options.
    An array with placeholder value works as placeholder when
    input is disabled.
  */
	const modelsValues = useCallback(
		() => (formattedModels.length ? mapValuesToSelect(model, formattedModels) : MAKE_MODEL_PLACEHOLDER_VALUE),
		[formattedModels, model],
	);

	const optionsChangedCount = useValueChangedCount(formattedModels);
	const isDisabled = formattedModels.length === 0;

	return (
		<MultiSelect
			key={optionsChangedCount} // Re-mounting because MultiSelect is not able to change options once initialized
			searchBar
			id="multiselect-models"
			inputProps={{
				'data-testid': 'model-multi-select',
				disabled: isDisabled,
				onBlur: onModelSelectBlur,
				onFocus: onModelDropdownFocus,
				onSelect: onModelDropdownSelect,
			}}
			label={T.translate('vehicleList.filters.model')}
			onChange={(e) => onModelSelectChange(e, modelConfigId)}
			options={formattedModels}
			placeholder={T.translate('vehicleList.filters.modelPlaceholder')}
			theme={APP_THEME}
			value={modelsValues()}
		/>
	);
};

export default ModelMultiSelect;
